import { capitalize } from 'inflected';
import { DARK_CHANNEL_SPEND_SHARE_PCT } from '../dtc-commerce-mmm-constants';
import { CPTBRow, HierarchyLevel, HierarchyLevelConst } from '../dtc-commerce-mmm-types';

export interface AggregatedChannel {
  groupKey: string;
  displayName: string;
  channel: string;
  platform: string;
  mediaType: string;
  isDark: boolean;
  // True if any feature in this group has an incrementality test as a prior.
  hasIncrementalityTest: boolean;
  rawSpend: number;
  effectiveSpend: number;
  effectiveNormalized: number;
  ciLo: number;
  ciHi: number;
  spendShare: number;
  effectShare: number;
}

export const formatFieldName = (field: string): string => {
  if (!field) return '';
  return field
    .split('_')
    .map(w => (w ? capitalize(w) : w))
    .join(' ');
};

export const getAggregatedMediaType = (mediaTypes: string[]): string => {
  const unique = Array.from(new Set(mediaTypes.filter(Boolean)));
  if (unique.length === 0) return '';
  if (unique.length === 1) return unique[0];
  if (unique.includes('demand_generation') && unique.includes('demand_capture')) return 'mixed';
  return unique[0];
};

const getGroupKey = (row: CPTBRow, level: HierarchyLevel): string => {
  switch (level) {
    case HierarchyLevelConst.CHANNEL:
      return row.channel;
    case HierarchyLevelConst.PLATFORM:
      return `${row.channel}|${row.platform}`;
    case HierarchyLevelConst.TACTIC:
      return `${row.channel}|${row.platform}|${row.tactic}`;
    case HierarchyLevelConst.BRAND_NONBRAND:
      return row.name;
    default:
      return row.channel;
  }
};

const getDisplayName = (row: CPTBRow, level: HierarchyLevel): string => {
  switch (level) {
    case HierarchyLevelConst.CHANNEL:
      return formatFieldName(row.channel);
    case HierarchyLevelConst.PLATFORM: {
      const parts = [formatFieldName(row.channel)];
      if (row.platform) {
        parts.push(formatFieldName(row.platform));
      }
      return parts.join(', ');
    }
    case HierarchyLevelConst.TACTIC: {
      const parts = [formatFieldName(row.channel)];
      if (row.platform) {
        parts.push(formatFieldName(row.platform));
      }
      if (row.tactic) {
        parts.push(formatFieldName(row.tactic));
      }
      return parts.join(', ');
    }
    case HierarchyLevelConst.BRAND_NONBRAND: {
      const parts = [row.channel, row.platform, row.tactic, row.brandNonbrand]
        .filter(Boolean)
        .map(formatFieldName);
      return parts.length > 0 ? parts.join(', ') : formatFieldName(row.name);
    }
    default:
      return formatFieldName(row.channel);
  }
};

export const aggregateChannels = (rows: CPTBRow[], level: HierarchyLevel): AggregatedChannel[] => {
  const groups = new Map<string, { rows: CPTBRow[]; displayName: string }>();

  rows.forEach(row => {
    const key = getGroupKey(row, level);
    const group = groups.get(key) ?? { rows: [], displayName: getDisplayName(row, level) };
    group.rows.push(row);
    groups.set(key, group);
  });

  let totalRawSpend = 0;
  let totalEffectiveNormalized = 0;
  groups.forEach(({ rows: groupRows }) => {
    groupRows.forEach(r => {
      totalRawSpend += r.rawSpend;
      totalEffectiveNormalized += r.effectiveNormalized;
    });
  });

  const result: AggregatedChannel[] = [];
  groups.forEach(({ rows: groupRows, displayName }, groupKey) => {
    const rawSpend = groupRows.reduce((s, r) => s + r.rawSpend, 0);
    const effectiveSpend = groupRows.reduce((s, r) => s + r.effectiveSpend, 0);
    const effectiveNormalized = groupRows.reduce((s, r) => s + r.effectiveNormalized, 0);
    const ciLo = groupRows.reduce((s, r) => s + (r.ciLo || 0), 0);
    const ciHi = groupRows.reduce((s, r) => s + (r.ciHi || 0), 0);
    const firstRow = groupRows[0];

    const spendShare = totalRawSpend > 0 ? (rawSpend / totalRawSpend) * 100 : 0;
    // "Dark" means negligible investment in the selected window, not strictly zero spend —
    // a channel running on scraps is as uninformative to current decisions as one that is off.
    const isDark = spendShare < DARK_CHANNEL_SPEND_SHARE_PCT;

    result.push({
      groupKey,
      displayName,
      channel: firstRow.channel,
      platform: firstRow.platform,
      mediaType: getAggregatedMediaType(groupRows.map(r => r.mediaType)),
      isDark,
      hasIncrementalityTest: groupRows.some(r => r.incrementalityTest),
      rawSpend,
      effectiveSpend,
      effectiveNormalized,
      ciLo,
      ciHi,
      spendShare,
      effectShare:
        totalEffectiveNormalized > 0 ? (effectiveNormalized / totalEffectiveNormalized) * 100 : 0
    });
  });

  return result.sort((a, b) => b.effectShare - a.effectShare);
};

export const getEfficiency = (ch: AggregatedChannel, isRevenue: boolean): number | null => {
  if (isRevenue) {
    return ch.effectiveSpend > 0 ? ch.effectiveNormalized / ch.effectiveSpend : null;
  }
  return ch.effectiveNormalized > 0 ? ch.effectiveSpend / ch.effectiveNormalized : null;
};

export interface DualChannel {
  groupKey: string;
  displayName: string;
  channel: string;
  platform: string;
  mediaType: string;
  isDark: boolean;
  hasIncrementalityTest: boolean;
  directSpend: number;
  actualizedSpend: number;
  directContribution: number;
  actualizedContribution: number;
  actualizedContributionCiLo: number;
  actualizedContributionCiHi: number;
  directSpendShare: number;
  actualizedSpendShare: number;
  directContributionShare: number;
  actualizedContributionShare: number;
  efficiencyActualized: number | null;
  efficiencyDirect: number | null;
}

export const aggregateDualChannels = (
  actualizedRows: CPTBRow[],
  rawRows: CPTBRow[],
  level: HierarchyLevel,
  isRevenue: boolean
): DualChannel[] => {
  const actAgg = aggregateChannels(actualizedRows, level);
  const rawAgg = aggregateChannels(rawRows, level);

  const actByKey = new Map(actAgg.map(c => [c.groupKey, c]));
  const rawByKey = new Map(rawAgg.map(c => [c.groupKey, c]));

  const allKeys = new Set<string>([...actByKey.keys(), ...rawByKey.keys()]);
  const result: DualChannel[] = [];

  allKeys.forEach(key => {
    const a = actByKey.get(key);
    const r = rawByKey.get(key);
    const reference = a ?? r;
    if (!reference) return;

    result.push({
      groupKey: reference.groupKey,
      displayName: reference.displayName,
      channel: reference.channel,
      platform: reference.platform,
      mediaType: reference.mediaType,
      isDark: reference.isDark,
      hasIncrementalityTest: Boolean(a?.hasIncrementalityTest || r?.hasIncrementalityTest),
      directSpend: r?.rawSpend ?? 0,
      actualizedSpend: a?.effectiveSpend ?? 0,
      directContribution: r?.effectiveNormalized ?? 0,
      actualizedContribution: a?.effectiveNormalized ?? 0,
      actualizedContributionCiLo: a?.ciLo ?? 0,
      actualizedContributionCiHi: a?.ciHi ?? 0,
      directSpendShare: r?.spendShare ?? 0,
      actualizedSpendShare: a?.spendShare ?? 0,
      directContributionShare: r?.effectShare ?? 0,
      actualizedContributionShare: a?.effectShare ?? 0,
      efficiencyActualized: a ? getEfficiency(a, isRevenue) : null,
      efficiencyDirect: r ? getEfficiency(r, isRevenue) : null
    });
  });

  return result;
};
